'use client';

import { useEffect, useRef, useState } from 'react';
import { PromptBit, PromptBitCategory } from '@/lib/types';
import {
  appendBlock,
  appendSection,
  parseSections,
  removeSection,
  replaceSectionText,
  stripComments,
} from '@/lib/promptSections';

/**
 * Chip row over the builder prompt: one chip per parsed `--` section, with ◀ ▶ bit cycling
 * and × removal per section, a "+ Add" dropdown for absent (or repeatable) categories, and
 * a live word count of the comment-stripped prompt. All mutations flow through
 * onPromptChange — the raw textarea string stays the single source of truth.
 *
 * Keyboard (plan §4): chips are focusable; ↑↓ move section focus, ←→ cycle bits.
 */

interface SectionRibbonProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  categories: PromptBitCategory[];
  bitsBySlug: Record<string, PromptBit[]>;
  // Fired when a chip is clicked or keyboard-focused (bit inserts target that occurrence)
  onSectionFocus?: (slug: string, occurrence: number) => void;
}

const WORD_WARN_THRESHOLD = 250;

// Slugs that make up the "Character block" quick-add (filtered to existing categories)
const CHARACTER_BLOCK_SLUGS = ['character', 'clothing', 'pose'];

// 0-based index of the bit whose text matches the section text (trimmed), or -1
function matchBitIndex(bits: PromptBit[], text: string): number {
  const trimmed = text.trim();
  return bits.findIndex((b) => b.text.trim() === trimmed);
}

export default function SectionRibbon({ prompt, onPromptChange, categories, bitsBySlug, onSectionFocus }: SectionRibbonProps) {
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);
  // Roving focus across the category chips (↑↓ move focus, ←→ cycle bits)
  const chipRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Close the + Add dropdown on outside click
  useEffect(() => {
    if (!addOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) {
        setAddOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [addOpen]);

  const sections = parseSections(prompt);
  const categoryBySlug = new Map(categories.map((c) => [c.slug, c]));

  const slugCounts = new Map<string, number>();
  for (const section of sections) {
    if (section.slug !== null) {
      slugCounts.set(section.slug, (slugCounts.get(section.slug) ?? 0) + 1);
    }
  }

  const orderedSlugs = [...categories]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => c.slug);

  // Categories not yet in the prompt, plus multi categories again (numbered)
  const addOptions = categories
    .filter((c) => !slugCounts.get(c.slug) || c.multi)
    .map((c) => {
      const count = slugCounts.get(c.slug) ?? 0;
      return { slug: c.slug, label: count > 0 ? `${c.label} #${count + 1}` : c.label };
    });

  const handleCycle = (slug: string, occurrence: number, text: string, direction: 1 | -1) => {
    const bits = bitsBySlug[slug] ?? [];
    if (bits.length === 0) return;
    const currentIndex = matchBitIndex(bits, text);
    // From -1 (unmatched text), ▶ goes to the first bit; both directions wrap.
    const nextIndex =
      direction === 1
        ? (currentIndex + 1) % bits.length
        : currentIndex <= 0
          ? bits.length - 1
          : currentIndex - 1;
    onPromptChange(replaceSectionText(prompt, slug, occurrence, bits[nextIndex].text));
  };

  const handleRemove = (slug: string, occurrence: number) => {
    onPromptChange(removeSection(prompt, slug, occurrence));
  };

  const handleAdd = (slug: string) => {
    onPromptChange(appendSection(prompt, slug, '', orderedSlugs));
    setAddOpen(false);
  };

  // Character block quick-add: only slugs that actually exist, in sort_order
  const blockSlugs = [...categories]
    .filter((c) => CHARACTER_BLOCK_SLUGS.includes(c.slug))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => c.slug);
  const hasCharacterBlock = blockSlugs.includes('character');
  const blockSubtitle = blockSlugs.filter((s) => s !== 'character').map((s) => `+ ${s}`).join(' ');

  const handleAddBlock = () => {
    onPromptChange(appendBlock(prompt, blockSlugs, orderedSlugs));
    setAddOpen(false);
  };

  // Chips of known categories, in render order (the keyboard focus ring)
  const focusableKeys = sections
    .filter((s) => s.slug !== null && categoryBySlug.has(s.slug))
    .map((s) => `${s.slug}-${s.occurrence}`);

  const handleChipKeyDown = (
    e: React.KeyboardEvent,
    index: number,
    slug: string,
    occurrence: number,
    text: string
  ) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const total = focusableKeys.length;
      if (total === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      chipRefs.current[(index + delta + total) % total]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      handleCycle(slug, occurrence, text, e.key === 'ArrowRight' ? 1 : -1);
    }
  };

  const wordCount = stripComments(prompt).trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {sections.map((section) => {
        if (section.slug === null) {
          return (
            <span
              key="free"
              className="px-2 py-1 rounded-lg text-xs bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-muted)]"
            >
              Free text
            </span>
          );
        }

        const category = categoryBySlug.get(section.slug);
        const chipKey = `${section.slug}-${section.occurrence}`;

        if (!category) {
          return (
            <span
              key={chipKey}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg text-xs bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-muted)]"
            >
              {section.slug}
              <button
                type="button"
                onClick={() => handleRemove(section.slug!, section.occurrence)}
                className="px-0.5 text-[var(--text-muted)] hover:text-[var(--error)] transition-subtle cursor-pointer"
                title={`Remove ${section.slug} section`}
              >
                ×
              </button>
            </span>
          );
        }

        const bits = bitsBySlug[section.slug] ?? [];
        const label =
          (slugCounts.get(section.slug) ?? 0) > 1
            ? `${category.label} #${section.occurrence + 1}`
            : category.label;
        const focusIndex = focusableKeys.indexOf(chipKey);
        const bitIndex = matchBitIndex(bits, section.text);

        return (
          <span
            key={chipKey}
            tabIndex={0}
            ref={(el) => { chipRefs.current[focusIndex] = el; }}
            onClick={() => onSectionFocus?.(section.slug!, section.occurrence)}
            onFocus={() => onSectionFocus?.(section.slug!, section.occurrence)}
            onKeyDown={(e) => handleChipKeyDown(e, focusIndex, section.slug!, section.occurrence, section.text)}
            title="↑↓ move section focus, ←→ cycle bits"
            className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg text-xs bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)]"
          >
            <span className="font-medium text-[var(--text-primary)]">{label}</span>
            <button
              type="button"
              onClick={() => handleCycle(section.slug!, section.occurrence, section.text, -1)}
              disabled={bits.length === 0}
              className="px-0.5 text-[var(--text-muted)] hover:text-[var(--accent)] transition-subtle cursor-pointer disabled:opacity-30 disabled:cursor-default"
              title={`Previous ${category.label} bit`}
            >
              ◀
            </button>
            {bits.length > 0 && (
              <span className="text-[10px] text-[var(--text-muted)] tabular-nums select-none">
                {bitIndex === -1 ? '–' : bitIndex + 1}/{bits.length}
              </span>
            )}
            <button
              type="button"
              onClick={() => handleCycle(section.slug!, section.occurrence, section.text, 1)}
              disabled={bits.length === 0}
              className="px-0.5 text-[var(--text-muted)] hover:text-[var(--accent)] transition-subtle cursor-pointer disabled:opacity-30 disabled:cursor-default"
              title={`Next ${category.label} bit`}
            >
              ▶
            </button>
            <button
              type="button"
              onClick={() => handleRemove(section.slug!, section.occurrence)}
              className="px-0.5 text-[var(--text-muted)] hover:text-[var(--error)] transition-subtle cursor-pointer"
              title={`Remove ${category.label} section`}
            >
              ×
            </button>
          </span>
        );
      })}

      {(addOptions.length > 0 || hasCharacterBlock) && (
        <div ref={addRef} className="relative">
          <button
            type="button"
            onClick={() => setAddOpen((o) => !o)}
            className="px-2 py-1 rounded-lg text-xs bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-subtle cursor-pointer"
          >
            + Add
          </button>
          {addOpen && (
            <div className="absolute left-0 top-full mt-1 z-30 min-w-[10rem] py-1 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] shadow-xl">
              {hasCharacterBlock && (
                <button
                  type="button"
                  onClick={handleAddBlock}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] transition-subtle cursor-pointer"
                >
                  Character block
                  {blockSubtitle && (
                    <span className="block text-[10px] text-[var(--text-muted)]">{blockSubtitle}</span>
                  )}
                </button>
              )}
              {addOptions.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleAdd(option.slug)}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] transition-subtle cursor-pointer"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <span
        className={`ml-auto text-xs ${wordCount > WORD_WARN_THRESHOLD ? 'text-[#fbbf24]' : 'text-[var(--text-muted)]'}`}
      >
        {wordCount} words
      </span>
    </div>
  );
}
